import React, { Component } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { withNamespaces } from "react-i18next";
import { Row, Col, Card, CardBody, CardTitle, Media, Button, Badge } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import moment from "moment";
import {
  getTaskDashboardStats,
  getTaskTrend,
  getWorkstationPositionsStatus,
  getPlcSafetyDashboardStats,
} from "../../store/actions";
import { map_pos_status } from "../../constants/map_fields";

// P01021-139 Fase 2: KPIs e indicadores rápidos del Dashboard, complementando las tablas
// paginadas de TasksBoard con una vista de resumen (backend ya agrega en SQL, sin traer filas).
function formatDuration(seconds) {
  if (seconds === null || seconds === undefined) return "—";
  const total = Math.round(seconds);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
}

function formatElapsedSince(dateStr) {
  if (!dateStr) return "—";
  return formatDuration(moment().diff(moment(dateStr), "seconds"));
}

class DashboardStats extends Component {
  constructor(props) {
    super(props);
    this.state = {
      user: JSON.parse(localStorage.getItem("authUser")),
    };
  }

  componentDidMount() {
    this.fetchAll();
  }

  fetchAll() {
    this.props.getTaskDashboardStats(this.state.user);
    this.props.getTaskTrend(this.state.user);
    this.props.getWorkstationPositionsStatus(this.state.user);
    this.props.getPlcSafetyDashboardStats(this.state.user);
  }

  refresh(event) {
    event.preventDefault();
    this.fetchAll();
  }

  groupPositionsByWorkstation(positions) {
    const groups = [];
    const index = {};
    (positions || []).forEach((position) => {
      if (!index[position.ws_id]) {
        index[position.ws_id] = { ws_id: position.ws_id, positions: [] };
        groups.push(index[position.ws_id]);
      }
      index[position.ws_id].positions.push(position);
    });
    return groups;
  }

  buildTrendChart(trend) {
    const categories = (trend?.days || []).map((day) => moment(day).format("DD/MM"));
    const created = trend?.created || [];
    const completed = trend?.completed || [];
    const series = [
      { name: "Creadas", type: "column", data: created },
      { name: "Finalizadas", type: "line", data: completed },
    ];
    // ApexCharts reparte el eje Y en 6 pasos por defecto, sean cuales sean los valores — con
    // datos pequeños (0/1) eso da pasos fraccionarios que redondean a números repetidos.
    // Fijamos un tick por cada valor entero real, con tope para no saturar si el máximo es alto.
    const maxValue = Math.max(1, ...created, ...completed);
    const tickAmount = Math.min(maxValue, 8);
    const options = {
      chart: { height: 280, type: "line", stacked: false, toolbar: { show: false } },
      stroke: { width: [0, 3], curve: "smooth" },
      plotOptions: { bar: { columnWidth: "40%" } },
      colors: ["#556ee6", "#34c38f"],
      xaxis: { categories },
      yaxis: { min: 0, max: maxValue, tickAmount, labels: { formatter: (val) => Math.round(val) } },
    };
    return { series, options };
  }

  render() {
    const { stats, trend, wsPositions, plcStats } = this.props;
    const { series, options } = this.buildTrendChart(trend);
    const wsPositionGroups = this.groupPositionsByWorkstation(wsPositions);
    const kpis = [
      { title: "Tareas activas", value: stats?.activeTasks ?? "—", icon: "bx-list-ul", color: "primary" },
      { title: "Finalizadas hoy", value: stats?.completedToday ?? "—", icon: "bx-check-circle", color: "success" },
      { title: "Canceladas hoy", value: stats?.cancelledToday ?? "—", icon: "bx-x-circle", color: "danger" },
    ];

    return (
      <React.Fragment>
        <Row>
          <Col>
            <CardTitle className="mb-1">{"Resumen"}</CardTitle>
          </Col>
          <Col className="refreshButton">
            <Button onClick={(event) => this.refresh(event)} color="primary" className="btn btn-primary">
              {" "}
              <i size="lg" className="mdi  mdi-refresh"></i>
            </Button>
          </Col>
        </Row>
        <Row>
          {kpis.map((kpi, key) => (
            <Col md={4} key={"_kpi_" + key}>
              <Card className="mini-stats-wid">
                <CardBody>
                  <Media>
                    <Media body>
                      <p className="text-muted font-weight-medium">{kpi.title}</p>
                      <h4 className="mb-0">{kpi.value}</h4>
                    </Media>
                    <div className={"mini-stat-icon avatar-sm rounded-circle bg-" + kpi.color + " align-self-center"}>
                      <span className="avatar-title">
                        <i className={"bx " + kpi.icon + " font-size-24"}></i>
                      </span>
                    </div>
                  </Media>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <CardTitle className="mb-3">{"Tendencia de tareas (7 días)"}</CardTitle>
                <ReactApexChart options={options} series={series} type="line" height={280} />
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col lg={6}>
            <Card>
              <CardBody>
                <CardTitle className="mb-3">{"Estado de posiciones"}</CardTitle>
                {wsPositionGroups.length === 0 && <p className="text-muted">{"Sin datos"}</p>}
                {wsPositionGroups.map((group) => (
                  <div key={group.ws_id} className="mb-2">
                    <div className="text-muted font-size-12">{group.ws_id}</div>
                    <div className="d-flex flex-wrap" style={{ gap: "6px" }}>
                      {group.positions.map((position) => {
                        const meta = map_pos_status[position.rcs_position_status] || map_pos_status[null];
                        return (
                          <Badge key={position.ws_pos} color={meta.color} className={"badge-soft-" + meta.color} pill>
                            {position.ws_pos} {meta.name}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </CardBody>
            </Card>
          </Col>
          <Col lg={6}>
            <Card>
              <CardBody>
                <CardTitle className="mb-3">{"Seguridad PLC"}</CardTitle>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted">{"Causas activadas hoy"}</span>
                  <span className="font-weight-bold">{plcStats?.causesActivatedToday ?? "—"}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted">{"Duración de incidentes hoy"}</span>
                  <span className="font-weight-bold">{formatDuration(plcStats?.incidentDurationTodaySeconds)}</span>
                </div>
                <div className="text-muted mb-1">{"Zonas bloqueadas ahora"}</div>
                {(plcStats?.blockedZonesNow || []).length === 0 && (
                  <p className="text-success mb-0">{"Ninguna"}</p>
                )}
                {(plcStats?.blockedZonesNow || []).map((zone) => (
                  <div key={zone.zone_code} className="d-flex justify-content-between align-items-center mb-1">
                    <Badge color="danger">{zone.zone_name || zone.zone_code}</Badge>
                    <span className="text-muted">{formatElapsedSince(zone.blocked_since)}</span>
                  </div>
                ))}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </React.Fragment>
    );
  }
}

const mapStateprops = (state) => {
  return {
    stats: state.getTaskDashboardStats.stats,
    trend: state.getTaskTrend.trend,
    wsPositions: state.getWorkstationPositionsStatus.positions,
    plcStats: state.getPlcSafetyDashboardStats.stats,
  };
};

export default withRouter(
  connect(mapStateprops, {
    getTaskDashboardStats,
    getTaskTrend,
    getWorkstationPositionsStatus,
    getPlcSafetyDashboardStats,
  })(withNamespaces()(DashboardStats))
);
